import { Track } from "@/src/lib/tracking";
import { Message, useChat } from "@ai-sdk/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import {
  type InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { OspToast } from "@ospweb/sdk-ui";
import { useMemoizedFn } from "ahooks";
import { useSnapshot } from "valtio";
import { isEmpty } from "lodash-es";
import { pubsub } from "utils";
import { decodeURIComponentSafe } from "../utils/decodeURIComponentSafe";
import {
  resolveChatKeyParts,
  resolveChatPrefillInput,
} from "../utils/chatUtils/chatPrefillInput";
import {
  invalidateChatDetailQueries,
  resetChatDetailPagination,
} from "../utils/chatDetailInvalidateQueries";
import { UpgradeReminderModalCreateAble } from "../components/modal/UpgradeReminder";
import {
  getAgentDataByType,
  groupByDeepSearch,
  groupNonReportAgent,
  groupReportAgent,
} from "../utils/groupByAgent";
import { errorCode, getErrors } from "../config/error";
import { decodeString, isEncoded } from "../utils/stringEncoder";
import { deepSearch, streamReport } from "../components/Chat/const";
import { ChatStore } from "@store/modules/chat";
import { UserStore } from "@store/modules/user";
import { rootStore } from "@store/rootStore";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";
import { useSearchEquity } from "./useSearchEquity";
import { appendSaveGoalConfirmedWithChatDetailSync } from "./onboarding/appendSaveGoalConfirmedMessages";
import { IChatInfoProps } from "./useCreateChat";
import { useAgentThinking } from "./useAgentThinking";
import { useRequestHeader } from "./useRequestHeader";
import { useChatCreditCheck } from "./useChatCreditCheck";
import { clearQuizResultContext } from "./onboarding/quizResultContextStorage";
import { ONBOARDING_QUIZ_CONTEXT_MARKER } from "./onboarding/fetchQuizResult";
import { formatMarkdownSpaces, formatMsgQuotes } from "@utils/formatMsgQuotes";
// import { resolveInteractiveToolCalls } from "@utils/chatUtils/resolveInteractiveToolCalls";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { I18nUtil } from "@utils/i18n";
import { LOCAL_STORAGE_KEYS, QUERY_KEYS } from "@config/constant";
import { ROUTER_PATH } from "@config/router";
import { chatClientApiBaseUrl } from "@config/config";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { resolveAllowedChatMode } from "@utils/chatMode";
import {
  CHAT_EVENTS,
  buildChatQuestionPayload,
} from "@components/Chat/chatTrackingEvents";
import { isReferenceOnlyMessage } from "@utils/chatUtils/parseChatMessage";
import { useShellRouter } from "./useShellAwareRouter";
import type { SenderAttachment } from "../components/Sender/type";

/** chatsDetail 分页每页条数 */
const CHAT_DETAIL_PAGE_SIZE = 10;

type ChatDetailPageParam = {
  limit: number;
  /** 第二页起传当前已加载最早一条消息的 id；首屏不传 */
  startId?: string;
};

type ChatDetailPageResult = {
  messages?: any[];
  chat?: Record<string, unknown>;
};

/** select 合并多页后的结构与单页详情一致 */
type ChatDetailMergedData = ChatDetailPageResult;

export enum deepSearchStatus {
  PROCESS = "process",
  FINISH = "finish",
}

export type IToolCallList = {
  flow?: string;
  id?: string;
  step?: number;
  header?: string;
  summary?: string;
  toolCallId: string;
  toolName?: string;
  args?: Record<string, any>;
  result?: {
    urls?: {
      url: string;
      title: string;
      description: string;
      favicon: string;
      domain?: string;
    }[];
  };
  state?: string;
  deepsearch?: boolean;
};

const resolveChatId = (
  propsChatId?: string,
  paramsId?: string | string[],
): string => {
  if (propsChatId) {
    return propsChatId;
  }
  if (Array.isArray(paramsId)) {
    return paramsId[0] ?? "";
  }
  return paramsId ?? "";
};

// key = `${encodeQuestion}_${sign}`，sign 分段（type / encodedSource）是紧凑 token
// （纯词或 base64，或 `chat:symbol#X+id#Y` 形态）。裸问题文本误入 key 参数时（历史 bug：
// 含下划线的协议消息如 plan_checkup_report 被 split 误拆），分段是正文碎片 —— 形状校验
// 不过，按「无 sign」处理，避免污染消息 type 字段。
const SIGN_SEGMENT_RE = /^[A-Za-z0-9+/.=%:#-]+$/;
const isSignSegment = (segment?: string): segment is string =>
  !!segment && SIGN_SEGMENT_RE.test(segment);

const isProtocolOnlyMessage = (value?: string): boolean =>
  isReferenceOnlyMessage(value);

export const useChats = (props?: IChatInfoProps & { scene?: string }) => {
  const pathname = usePathname();

  const normalizeCustomParams = (customParams?: Record<string, any>) => {
    if (!customParams) {
      return {};
    }
    return customParams;
  };

  const errors = getErrors();
  const { scope = "page", scene } = props || {};
  const params = useParams<{ locale: string; chatId: string }>();
  const [id, setId] = useState("");
  const { fingerprint } = useSnapshot(UserStore);
  const { initChat } = useSnapshot(ChatStore);
  const { chatApi } = useContext(OspApiClientContext);
  const queryClient = useQueryClient();
  const router = useShellRouter();
  const { agentKeys } = useAgentThinking();

  const { headers } = useRequestHeader();

  const pendingSubmitRef = useRef<string | null>(null);
  // 用于稳定化 formatMessages 输出，防止 chatDetail 替换 stream messages 时因
  // id/agents/content 数据源切换导致 React key 变更（remount）或内容闪屏
  const prevFormatMessagesRef = useRef<any[]>([]);

  const searchParams = useSearchParams();
  const key = props?.key || searchParams.get("key");
  const tpl = props?.tpl || searchParams.get("tpl") || undefined;
  const cacheQuestion =
    props?.cacheQuestion || searchParams.get("cacheQuestion") || undefined;

  const { getCreditConfig } = useSearchEquity();
  const { checkCredit } = useChatCreditCheck();
  // 解析 key 参数，兼容原有逻辑并支持包含多个 _ 的 projectId
  const keyParts = resolveChatKeyParts(key);
  const name = isEncoded(keyParts[0]) ? decodeString(keyParts[0]) : keyParts[0];
  const hasSign =
    keyParts.length >= 2 &&
    isSignSegment(keyParts[1]) &&
    (keyParts.length < 3 || isSignSegment(keyParts[2]));
  const type = hasSign ? keyParts[1] : undefined;
  const source = hasSign ? keyParts[2] || "introduction" : undefined;
  const projectId =
    hasSign && keyParts.length > 3 ? keyParts.slice(3).join("_") : "";

  const { isLoggedIn, isLoading: loginLoading } = useSnapshot(rootStore.login);
  const storeId =
    (typeof window !== "undefined"
      ? localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID)
      : null) || "";
  const chatId = resolveChatId(props?.chatId || storeId, params?.chatId);

  /**
   * `chatId` 记在 ref 里供埋点用 —— **不再写成 OpenReplay 的 session metadata**。
   *
   * 改之前这里有一处 `tracker.setMetadata("aiChatId", chatId)`，绕过了 `Track.context` 的
   * §2.4 白名单（`aiChatId` 不在那 18 个 key 里，走门面会被丢掉，所以当初直连了下层）。
   * 那个做法错在三点，任一条都够：
   *
   * 1. metadata 是**会话级、只能覆盖** —— 用户切会话时值被反复改写，后台只留得下最后一个 chatId。
   *    **一个会中途变化的值不该做会话属性。**
   * 2. OpenReplay 自定义 metadata 只有 **10 个固定槽位**，规范规划的 18 个 key 已经超预算。
   * 3. 按 chat 维度查询，事件字段比会话属性好用得多。
   *
   * 现在它作为 `chat_id` 挂在 `chat_message_sent` / `chat_response_done` 上。
   * 用 ref 而不是直接闭包 `chatId`：`onFinish` / `onError` 是 `useChat` 配置里的回调，
   * 拿到的是**首次渲染**那一份闭包，直接引用会读到过期的 chatId。
   */
  const chatIdRef = useRef(chatId);
  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  /** 本轮回复的起始时刻 —— 算 `chat_response_done.duration_ms`。null = 当前没有进行中的回复。 */
  const responseStartedAtRef = useRef<number | null>(null);

  /**
   * 一轮回复结束（正常完成或出错）。
   *
   * 改之前**两端都没有这个事件** —— 下游因此区分不了「用户发完消息正在等回复」和
   * 「这轮对话已经结束」，而这恰好是「能不能插话」最关键的一个区分，只能靠时间猜。
   *
   * 两条防重复的约束：
   * - `responseStartedAtRef` 为 null 表示这轮已经结算过（或压根没开始）→ 直接返回。
   *   `onFinish` 与 `onError` 在异常路径上可能都被调到，不去重会算两次。
   * - 结算后立刻置 null，把「一轮」这件事收成一次性的。
   */
  const emitChatResponseDone = useMemoizedFn(
    (opts: { isError: boolean; errorMessage?: string; stopped?: boolean }) => {
      const startedAt = responseStartedAtRef.current;
      if (startedAt === null) {
        return;
      }
      responseStartedAtRef.current = null;
      Track.event(CHAT_EVENTS.responseDone, {
        chat_id: chatIdRef.current,
        duration_ms: Math.round(Date.now() - startedAt),
        is_error: opts.isError,
        // 与 native 的同名字段对齐：用户主动停止与「跑完了」对「能不能插话」的含义不一样
        stopped: opts.stopped ?? false,
        // 只取前 200 字符：错误文案可能很长，且规范 §3.2 要求参数是 KB 级
        error_message: opts.errorMessage?.slice(0, 200),
      });
    },
  );

  useEffect(() => {
    try {
      const is_create_chat = localStorage.getItem("is_create_chat");
      const create_chat = localStorage.getItem("create_chat");
      if (!loginLoading && !isLoggedIn) {
        localStorage.removeItem("is_create_chat");
        localStorage.removeItem("create_chat");
      }
      if (create_chat && isLoggedIn) {
        if (is_create_chat) {
          setId(chatId);
          localStorage.removeItem("is_create_chat");
          localStorage.removeItem("create_chat");
        }
      } else {
        setId(chatId);
      }
    } catch (error) {
      Track.error(error, { tag: "Chat error", msg: "处理聊天ID时发生错误:" });
    }
  }, [chatId, loginLoading, isLoggedIn]);

  const {
    data: chatDetailData,
    isPending: chatDetailLoading,
    isFetchingPreviousPage: chatDetailFetchingPreviousPage,
    fetchPreviousPage: fetchChatDetailPreviousPage,
    hasPreviousPage: hasChatDetailPreviousPage,
  } = useInfiniteQuery<
    ChatDetailPageResult,
    Error,
    ChatDetailMergedData | undefined,
    readonly unknown[],
    ChatDetailPageParam
  >({
    queryKey: [QUERY_KEYS.CHAT_DETAIL, chatId],
    enabled:
      !!chatApi &&
      !!chatId &&
      !initChat &&
      !localStorage.getItem("create_chat") &&
      !ChatStore.loading,
    initialPageParam: {
      limit: CHAT_DETAIL_PAGE_SIZE,
    } satisfies ChatDetailPageParam,
    queryFn: async ({ pageParam }) => {
      const { limit, startId } = pageParam as ChatDetailPageParam;
      const res = await chatApi?.chats
        .chatsDetail(chatId, startId ? { limit, startId } : { limit })
        .catch((err) => {
          if (err?.error?.code === errorCode.CHAT_NOT_FOUND) {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
            router.replace(ROUTER_PATH.HOME);
          }
          return Promise.reject(err);
        });
      if (res?.error) {
        return Promise.reject(res?.error);
      }
      const data = res?.data?.data || {};
      const rawMessages = data?.messages ?? [];
      return {
        ...data,
        // 后端倒序返回，单页内转为阅读正序
        messages: [...rawMessages].reverse(),
      };
    },
    getNextPageParam: () => undefined,
    getPreviousPageParam: (firstPage, _, firstPageParam) => {
      const messages = firstPage?.messages ?? [];
      const pageSize =
        (firstPageParam as ChatDetailPageParam)?.limit ?? CHAT_DETAIL_PAGE_SIZE;
      if (messages.length < pageSize) {
        return undefined;
      }
      // 单页已 reverse 为正序，[0] 为时间最早；对应后端倒序返回的最后一条
      const startId = messages[0]?.id;
      if (!startId) {
        return undefined;
      }
      return {
        limit: pageSize,
        startId,
      };
    },
    select: (data: InfiniteData<ChatDetailPageResult>) => {
      const pages = data?.pages;
      if (!pages?.length) {
        return undefined;
      }
      const anchorPage = pages[pages.length - 1];
      const messages = pages.flatMap((page) => page?.messages ?? []);
      return {
        ...anchorPage,
        messages,
      };
    },
  });

  const chatDetail = useMemo(
    () => chatDetailData?.messages ?? [],
    [chatDetailData],
  );
  const lastMessage = chatDetail[chatDetail.length - 1];
  const referId =
    chatDetail && chatDetail.length > 1
      ? chatDetail[Math.max(0, chatDetail.length - 2)]?.messageId
      : undefined;
  // 获取最后一条重试状态，中间的不判断忽略，因为重试的消息会被放在最后面
  const reconnectId =
    lastMessage?.status === 2 ? lastMessage?.messageId : undefined;
  const {
    messages,
    input,
    handleInputChange,
    stop: streamStop,
    status,
    setInput,
    reload,
    append,
    error,
    setMessages,
  } = useChat({
    id: chatId || id || "",
    initialInput: resolveChatPrefillInput(initChat || ""),
    initialMessages: chatDetail as Message[],
    api: `${chatClientApiBaseUrl[process.env.NEXT_PUBLIC_ENV as any]}/stream`,
    // api: `${config.api.baseUrl.chat}/stream`,
    headers: {
      ...headers,
      guestId: fingerprint as string,
    },
    body: {
      id: chatId,
      // referId: referId ?? "",
    },
    experimental_prepareRequestBody: ({ messages, requestBody }) => {
      // 如果是 reload 操作（包含 retryId），不发送 messages
      if (
        requestBody &&
        typeof requestBody === "object" &&
        "retryId" in requestBody
      ) {
        return {
          id: chatId,
          retryId: (requestBody as any).retryId,
          // 不包含 messages，让服务端从存储中获取消息历史
        };
      }

      // 正常发送消息时，包含 messages 和原始 requestBody 中的其他字段
      return {
        id: chatId,
        messages: messages,
        ...(requestBody as object), // 保留原始 body 中的所有字段，包括 referId
      };
    },
    onFinish: () => {
      emitChatResponseDone({ isError: false });
      // 命中缓存，需要立刻清除is_submit_chat，防止再次出发发送问题逻辑
      localStorage.removeItem("is_submit_chat");
      // 防止submit | appendMessage之后，输入框清不掉的问题
      if (isProtocolOnlyMessage(input)) {
        setInput("");
      }
      ChatStore.initChat &&
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHAT_HISTORY] });

      // 调用详情接口，页面不会存在闪一下的问题，relatedQuestions从stream中获取；
      // 不能注释掉，存在的问题：
      // 1. 如果全部由前端缓存，缓存一个对话，再发起一个对话之后，thinking思考流，没有办法记录，只能存储最新的思考流数据；
      if (id) {
        invalidateChatDetailQueries(queryClient, id);
      }
      ChatStore.initChat = "";
      ChatStore.isTransform = false;
      pubsub.emit(pubSubKeyMap.AI_ANSWER_COMPLETED);
    },
    onError: async (error: Error) => {
      emitChatResponseDone({ isError: true, errorMessage: error.message });
      localStorage.removeItem("is_submit_chat");
      input && setInput("");
      Track.error(error, { tag: "chat page", msg: "[error]" });
      let code = null;
      let errorMessage = error.message;
      Track.log("error", "chat page", "[error message]", {
        cause: String(errorMessage),
      });
      if (
        errorMessage?.includes("Load failed") ||
        errorMessage?.includes("network error")
      ) {
        code = errorCode.NETWORK_ERROR;
      }
      try {
        // 尝试解析错误信息
        const errorData = JSON.parse(errorMessage);
        code = errorData.code;
        errorMessage = errorData.message || errorMessage;
      } catch (catchError) {
        Track.error(catchError, {
          tag: "onError",
          msg: "Error parsing error message:",
        });
      }
      if (
        (code === errorCode.LIMIT_TIMES ||
          code === errorCode.CREDITS_LIMITED) &&
        !isLoggedIn
      ) {
        // 保存当前的对话状态，以便登录后继续
        if (input) {
          ChatStore.recoverQuestionParams = {
            question: input,
            // 可以添加其他需要恢复的参数
          };
        }
        redirectToLoginPage();
        return;
      }
      //统一处理 stream 关于权益的 errorCode 对应的弹窗
      const errorConfig = getCreditConfig?.(code);
      if (!!errorConfig) {
        await UpgradeReminderModalCreateAble({
          customConfig: errorConfig,
        });
        return;
      }

      const errorItem = errors.find((item) => item.code === code);
      if (errorItem) {
        OspToast.error({
          content: errorItem.msg,
        });
        return;
      }
      stop({
        stopErrorCode: code,
      });
    },
  });

  const isLoading = status === "streaming" || status === "submitted";
  const streamMessage = messages?.[messages?.length - 1] || {};

  useEffect(() => {
    const init = async () => {
      if (loginLoading) {
        return;
      }
      const isSubmitChat = await localStorage.getItem("is_submit_chat");
      if (!isSubmitChat) {
        return;
      }
      let pendingValue: string | null = null;
      if (name || cacheQuestion) {
        const question = name || cacheQuestion || "";
        const decodeName = resolveChatPrefillInput(question);
        pendingValue = decodeName;
        setInput(decodeName);
        // 知道关键字和具体类型的时候，做转化。
        // 后端会把personName --> who is personName
        ChatStore.isTransform = !!question && !!type;
      }
      pendingSubmitRef.current = pendingValue;
    };
    init();
  }, [name, type, loginLoading, chatId, cacheQuestion]);

  useEffect(() => {
    if (initChat) {
      pendingSubmitRef.current = initChat;
      ChatStore.isTransform = !!(cacheQuestion || name) && !!type;
      setInput(initChat);
    }
  }, [cacheQuestion, initChat, name, type, headers]);

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      return;
    }
    if (isLoading || chatDetailLoading) {
      return;
    }
    if (
      chatDetail?.length &&
      !localStorage.getItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID)
    ) {
      setMessages(chatDetail as Message[]);
    }
    // 不能加loading依赖，否则页面会抖动
  }, [chatDetail, chatId, chatDetailLoading]);

  useEffect(() => {
    if (scope === "modal" && !!chatId) {
      // 弹窗内外状态统一
      ChatStore.loading = isLoading;
      ChatStore.stop = stop;
      ChatStore.streamMessage = streamMessage;
      if (isLoading) {
        ChatStore.showRecommendQuestion = false;
      }
    }
    return () => {
      // ChatStore.loading = false;
    };
  }, [isLoading, scope, streamMessage, chatId]);

  useEffect(() => {
    if (scope !== "modal" || !chatId) {
      return;
    }

    const handleDrawerStateChange = (isOpen: boolean) => {
      if (!isOpen) {
        resetChatDetailPagination(queryClient, chatId);
      }
    };

    const unsubscribe = pubsub.on(
      pubSubKeyMap.CHAT_DRAWER_STATE_CHANGE,
      handleDrawerStateChange,
    );

    return () => {
      unsubscribe?.();
      resetChatDetailPagination(queryClient, chatId);
    };
  }, [scope, chatId, queryClient]);

  useEffect(() => {
    const initSubmitChat = () => {
      const isSubmitChat = localStorage.getItem("is_submit_chat");
      if (
        !pendingSubmitRef.current ||
        !chatId ||
        loginLoading ||
        (isLoggedIn && isEmpty(headers)) ||
        !isSubmitChat
      ) {
        return;
      }
      if (pendingSubmitRef.current === input && input?.trim()) {
        pendingSubmitRef.current = null;
        handleSubmit(undefined, ChatStore.initCustomParams);
        ChatStore.initCustomParams = undefined;
        localStorage.removeItem("is_submit_chat");
      }
    };
    initSubmitChat();
  }, [input, chatId, isLoggedIn, headers]);

  // Associate OpenReplay session with AI chat id.

  const handleSubmit = async (
    e?: React.FormEvent,
    customParams?: Record<string, any>,
    inputOverride?: string,
  ) => {
    if (isLoading) {
      return;
    }
    // 一轮回复的计时起点。放在 isLoading 早返回**之后** —— 被 gate 掉的那次没有真的发起请求，
    // 若在它之前打点会把上一轮还没结算的起点覆盖掉，duration 就偏小了。
    responseStartedAtRef.current = Date.now();
    const submitContent =
      typeof inputOverride === "string" ? inputOverride : input;
    setTimeout(async () => {
      setInput("");
    }, 1000);
    const initParams: Record<string, any> = {};
    if (ChatStore.isTransform && type && source) {
      let sourceEncode = decodeURIComponentSafe(source);
      let id = projectId;
      try {
        sourceEncode = isEncoded(source) ? decodeString(source) : source;
      } catch (err) {
        Track.error(err, {
          tag: "chat detail",
          msg: "sourceEncode decodeString",
        });
      }
      try {
        id = isEncoded(projectId) ? decodeString(projectId) : projectId;
      } catch (err) {
        Track.error(err, { tag: "chat detail", msg: "id decodeString" });
      }
      initParams.type = id
        ? `${type}:${sourceEncode}:${id}`
        : `${decodeURIComponentSafe(type)}:${decodeURIComponentSafe(sourceEncode)}`;
    }
    if (ChatStore.isDeepsearch) {
      initParams.searchMode = 1;
    }
    if (ChatStore.tpl && tpl) {
      initParams.tpl = tpl;
      ChatStore.tpl = false;
    }
    localStorage.removeItem("deepSearchTime");
    localStorage.removeItem("thinkingSearchTime");
    e?.preventDefault();
    // 提问的**形态**（长度 / 是否带引用 / 是否 slash 指令）—— 故意不含原文，理由见
    // `buildChatQuestionPayload`。改之前这里是 `getTracker()?.event?.("chat", text)`：
    // 绕过门面（没过 redact 脱敏）+ 把用户输入原文当 payload。
    const questionText = submitContent?.trim?.();
    if (questionText) {
      Track.event(CHAT_EVENTS.question, buildChatQuestionPayload(questionText));
    }
    const currentMessage = {
      role: "user",
      content: submitContent,
      cache_content: cacheQuestion,
      language: I18nUtil.getBackgroundLanguageKey(params?.locale),
      chatMode: resolveAllowedChatMode(ChatStore.chatMode),
      ...initParams,
      ...normalizeCustomParams(customParams),
    };
    const bodyParams: any = {};
    if (pathname?.includes(ROUTER_PATH.ONBOARDING)) {
      bodyParams.interactiveToolOnly = true;
    }
    if (submitContent?.includes(ONBOARDING_QUIZ_CONTEXT_MARKER)) {
      clearQuizResultContext("first-stream-submitted");
    }
    await append(currentMessage as unknown as Message, {
      body: {
        ...bodyParams,
        messages: [currentMessage],
      },
    });
  };

  const appendToolMessage = useMemoizedFn(
    async ({
      toolCallId,
      chatId: targetChatId,
      content,
      toolName,
      interactiveToolOnly,
      attachments,
    }: {
      toolCallId: string;
      chatId: string;
      content: string;
      toolName?: string;
      attachments?: SenderAttachment[];
      interactiveToolOnly?: boolean;
    }): Promise<void> => {
      const currentMessage = {
        role: "tool",
        toolCallId,
        chatId: targetChatId,
        content,
        toolName,
        attachments,
        language: I18nUtil.getBackgroundLanguageKey(params?.locale),
        chatMode: resolveAllowedChatMode(ChatStore.chatMode),
      } as unknown as Message;

      await append(currentMessage, {
        body: {
          interactiveToolOnly,
          messages: [currentMessage],
        },
      });
    },
  );

  const appendSaveGoalConfirmed = useMemoizedFn(
    async ({
      toolCallId,
      chatId: targetChatId,
      toolName,
      payload,
    }: {
      toolCallId: string;
      chatId: string;
      toolName?: string;
      payload?: Record<string, unknown>;
    }): Promise<void> => {
      await appendSaveGoalConfirmedWithChatDetailSync(append, queryClient, {
        toolCallId,
        chatId: targetChatId,
        toolName,
        payload,
        language: I18nUtil.getBackgroundLanguageKey(params?.locale),
        chatMode: resolveAllowedChatMode(ChatStore.chatMode),
      });
    },
  );

  const appendMessage = useMemoizedFn(
    async ({
      inputValue,
      referId,
      customParams,
    }: {
      inputValue?: string;
      referId?: string;
      customParams?: Record<string, any>;
    }): Promise<void> => {
      const { blocked } = await checkCredit(inputValue);
      if (blocked) {
        return;
      }
      const initParams: Record<string, any> = {};
      if (ChatStore.isDeepsearch) {
        initParams.searchMode = 1;
      }
      const currentMessage = {
        role: "user",
        content: inputValue,
        language: I18nUtil.getBackgroundLanguageKey(params?.locale),
        chatMode: resolveAllowedChatMode(ChatStore.chatMode),
        ...initParams,
        ...normalizeCustomParams(customParams),
      } as unknown as Message;
      // 同上（appendMessage 路径）—— 形态维度，不含原文
      const appendQuestionText = inputValue?.trim?.();
      if (appendQuestionText) {
        Track.event(
          CHAT_EVENTS.question,
          buildChatQuestionPayload(appendQuestionText),
        );
      }
      const body: Record<string, any> = {
        messages: [currentMessage],
      };
      if (referId) {
        body.referId = referId;
      }
      setTimeout(async () => {
        // 提交完之后，输入框再输入的问题不能移除
        setInput("");
      }, 1000);
      await append(currentMessage, {
        body,
      });
    },
  );

  const stop = useMemoizedFn(
    async (props?: {
      stopErrorCode?: number | string;
      stopParams?: Record<string, any>;
    }) => {
      const { stopErrorCode, stopParams } = props || {};
      const params = isLoading
        ? { messageId: (streamMessage as any)?.messageId || streamMessage?.id }
        : {};
      // stop报错不可以清除数据，否则会导致调用详情接口，接口报错
      try {
        if (stopErrorCode === errorCode.NETWORK_ERROR) {
          params?.messageId &&
            reload({
              messages: [],
              reconnectId: params?.messageId,
            } as any);
          return;
        }
        Track.log("info", "useChats", "stop params:", {
          id: chatId,
          ...(!isEmpty(stopParams) ? stopParams : params),
        });
        await chatApi?.stream.stopCreate({
          id: chatId,
          ...(!isEmpty(stopParams) ? stopParams : params),
        });
      } catch (err) {
        Track.error(err, { tag: "usechat", msg: "stop error:" });
      }
      streamStop();
      /*
       * 这一轮也要结算 —— `streamStop()` 是 abort，**`onFinish` / `onError` 都不会被调到**，
       * 所以不在这里发的话：① 下游永远看不到这轮结束，「用户正在等回复」的状态一直挂着，
       * 直到下一轮把它顶掉；② `responseStartedAtRef` 不清，下一轮的 duration 会算成从上一轮起点。
       * native 侧 `stopStream()` 一直是发的（`stopped=true`），此前只有 web 漏了。
       */
      emitChatResponseDone({ isError: false, stopped: true });
      ChatStore.isTransform = false;
      ChatStore.loading = false;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHAT_HISTORY] });
      pubsub.emit(pubSubKeyMap.AI_ANSWER_COMPLETED);
      // todo deepseatch 报错数据返现
      const errorItem = errors.find((item) => item.code === stopErrorCode);
      if (errorItem) {
        OspToast.error({
          content: errorItem.msg,
        });
        return;
      }
      // stop的时候不刷新详情接口，initChat不重置，否则会导致chatId的调用，后端不能在暂停的时候实时刷新数据
      // 防止在其他地方(不在一个上下文)调用stop，暂停不了，重新发起请求的情况
      if (!isEmpty(stopParams)) {
        ChatStore.initChat = "";
        if (id) {
          invalidateChatDetailQueries(queryClient, id);
        }
      }
    },
  );

  const { formatMessages, collectedStreamMessageIds } = useMemo(() => {
    const streamMessageIdsSet = new Set<string>();
    // Protocol messages stay in the list. Message derives user input, action echo,
    // or silent entry presentation from their content shape.
    const formatted = messages.map((item: any, msgIndex: number) => {
      const { annotations = [] } = item || {};

      //  搜索模式 0=normal, 1=deepsearch, 2=agent
      const searchModeData = (annotations || []).find(
        (item: any) => typeof item.searchMode !== "undefined",
      );
      const streamMessageIdsData = annotations?.find(
        (item: any) => !!item.messageIds,
      );

      // 收集 streamMessageIds
      if (streamMessageIdsData?.messageIds) {
        streamMessageIdsData.messageIds.forEach((id: string) => {
          streamMessageIdsSet.add(id);
        });
      }

      const deepSearchData = (annotations || []).filter(
        (item: any) => item.agent === deepSearch,
      );
      const contentData = (annotations || []).filter(
        (item: any) => item.agent === streamReport,
      );
      const deepSearchReasoning =
        annotations?.filter((item: any) => item.agent === "reasoning") || [];
      const thinkingData = (annotations || []).filter((item: any) =>
        agentKeys.includes(item.agent),
      );
      const newContentData =
        groupReportAgent(contentData)?.[streamReport] || [];
      const isHidden = newContentData?.find(
        (item: any) => item?.data?.hidden === true,
      );
      if (isHidden) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID, chatId);
      }
      const contentInfo = getAgentDataByType(newContentData);
      const newDeepSearchData =
        groupByDeepSearch(deepSearchData)?.["deepsearch"] || [];
      const newDeepSearchReasoning =
        groupNonReportAgent(deepSearchReasoning)?.["reasoning"] || [];
      const newDeepSearchStepList = newDeepSearchData
        ?.filter((item) => item.type === "summary")
        ?.map((item) => item.data);
      // main steps
      const deepSearchStepList = newDeepSearchStepList;

      const stepList = deepSearchStepList?.map((item, index) => {
        const status =
          !!deepSearchStepList[index + 1]?.header ||
          item?.header === "Complete" ||
          !isLoading ||
          msgIndex !== messages.length - 1
            ? deepSearchStatus.FINISH
            : deepSearchStatus.PROCESS;
        return {
          ...item,
          status,
          id: `deep-search-${item?.step || item?.id}`,
        };
      });

      const content = item?.content ? item?.content : contentInfo?.text;
      // const interactiveToolCalls = resolveInteractiveToolCalls(
      //   item,
      //   annotations,
      // );

      // 记录 agent 在 thinkingData 中出现的顺序（第一次出现的顺序）
      const agentOrder: string[] = [];
      const seenAgents = new Set<string>();
      thinkingData.forEach((item: any) => {
        if (item?.agent && !seenAgents.has(item.agent)) {
          agentOrder.push(item.agent);
          seenAgents.add(item.agent);
        }
      });

      // 获取分组后的数据
      const groupedThinkingData = groupNonReportAgent(thinkingData);

      return {
        ...item,
        reasoning: item?.reasoning || newDeepSearchReasoning?.[0]?.data?.text,
        // 新流式输出回答数据在reportData中，通过chunk解析出来的，可刷新维持流式输出状态
        reportData: newContentData,
        toolCallList: newDeepSearchData?.map((item) => item.data),
        stepList,
        // 有历史sources/cards的优先取历史，否则流式依据新旧去出
        content: formatMarkdownSpaces(formatMsgQuotes(content)),
        sources: item?.sources ? item?.sources : contentInfo?.source || [],
        cards: item?.cards?.length > 0 ? item?.cards : contentInfo?.card,
        searchMode: searchModeData?.searchMode || item?.searchMode,
        agents: groupedThinkingData,
        agentTypes:
          item.agentTypes?.length > 0 ? item.agentTypes : agentOrder || [],
        relatedQuestions:
          item?.relatedQuestions?.length > 0
            ? item?.relatedQuestions
            : contentInfo?.["related-question"]?.relatedQuestions,
        // 流式期间 item.messageId 可能为空，从 annotation messageIds 补充；
        // 这样 prevFormatMessagesRef 里的 messageId 在 stream 完成时就已就位，
        // 后续 DB 数据进来时可以直接用 messageId 比对来识别同一条消息
        messageId: item.messageId ?? streamMessageIdsData?.messageIds?.[0],
      };
    });

    // 稳定化输出：当 key 显示字段相同时返回 prev 对象引用，避免两类闪屏：
    // 1. agents 从 stream 富数据 → DB annotations=null → {} 导致 Thinking 内容消失
    // 2. id 从 stream UUID → DB 数字 id，导致 React key 变更 → unmount + remount
    const stabilized = formatted.map((item: any, i: number) => {
      const prev = prevFormatMessagesRef.current[i];
      if (!prev || prev.role !== item.role) {
        return item;
      }

      // 始终保持 id 稳定：stream UUID → DB 数字 id 是同一条消息，id 不应该变
      // 复杂问题 sources/relatedQuestions 等字段可能与 stream 版本有差异，
      // 一旦 displaySame=false 就会返回带 DB id 的新对象 → React key 变 → remount → 闪屏
      const stableId = prev.id;

      // DB 同步过渡期：annotations=null → agents={}，但 agentTypes 非空（tabs 还在），
      // 保留 prev.agents 避免 Thinking 内容区域瞬间空白
      // !isLoading || !isLastItem：流式进行中时只放行最后一条（当前流式消息），
      // 历史消息（非最后一条）始终保护，防止新流式开始时上一条 agents 被清空闪屏
      const isLastItem = i === formatted.length - 1;
      const finalAgents =
        (!isLoading || !isLastItem) &&
        isEmpty(item.agents) &&
        !isEmpty(item.agentTypes) &&
        !isEmpty(prev.agents)
          ? prev.agents
          : item.agents;

      // Stream→DB 过渡：messageId 相同即为同一条消息，直接复用 stream 最终态。
      // prev.messageId 已在 formatted 阶段通过 annotation messageIds 补充，
      // 流式期间就有值；DB 数据进来后两侧 messageId 一致 → 冻结 prev，
      // sources/relatedQuestions 等字段的格式差异完全不会触发重渲染。
      // isLoading=true 时不做过渡（annotations 仍在变化），故加 !isLoading 守卫。
      if (
        !isLoading &&
        item.messageId != null &&
        prev.messageId != null &&
        String(item.messageId) === String(prev.messageId)
      ) {
        return prev;
      }

      // 比较 key 显示字段，判断能否复用整个 prev 引用（避免不必要的重渲染）
      const agentsSame =
        finalAgents === prev.agents ||
        (isEmpty(finalAgents) && isEmpty(prev.agents));
      const displaySame =
        agentsSame &&
        stableId === item.id && // id 已相同则无需稳定化，可直接比较其他字段
        prev.content === item.content &&
        JSON.stringify(prev.agentTypes) === JSON.stringify(item.agentTypes) &&
        JSON.stringify(prev.sources) === JSON.stringify(item.sources) &&
        JSON.stringify(prev.cards) === JSON.stringify(item.cards) &&
        JSON.stringify(prev.relatedQuestions) ===
          JSON.stringify(item.relatedQuestions) &&
        JSON.stringify(prev.interactiveToolCalls) ===
          JSON.stringify(item.interactiveToolCalls);

      if (displaySame) {
        // 完全相同：复用 prev 引用，同时更新 messageId 供 useAgentMessage 调用
        const updatedMessageId = item.messageId || prev.messageId;
        return updatedMessageId !== prev.messageId
          ? { ...prev, messageId: updatedMessageId }
          : prev;
      }

      // 内容有实质变化（sources/relatedQuestions 等更新）→ 正常返回新数据，
      // 但始终用 stableId 覆盖 id，防止 React key 变更
      const base =
        finalAgents !== item.agents ? { ...item, agents: finalAgents } : item;
      return stableId !== base.id ? { ...base, id: stableId } : base;
    });
    const isOnboardingScene = scene === "onboarding";
    const formatMessages = isOnboardingScene ? formatted : stabilized;

    prevFormatMessagesRef.current = isOnboardingScene ? stabilized : [];
    return {
      formatMessages,
      collectedStreamMessageIds: Array.from(streamMessageIdsSet),
    };
  }, [messages, scene, agentKeys, chatId, isLoading]);

  const messageIds = useMemo(() => {
    // 从 chatDetail 获取历史消息 ID
    const chatDetailIds = chatDetail?.map((item: any) => item.messageId) || [];

    // 合并并去重
    return Array.from(
      new Set(
        ChatStore.isDeepsearch && collectedStreamMessageIds?.length > 0
          ? collectedStreamMessageIds
          : [...chatDetailIds, ...collectedStreamMessageIds],
      ),
    );
  }, [chatDetail, collectedStreamMessageIds]);

  const chatReload = useMemoizedFn(() => {
    const bodyParams = reconnectId ? { reconnectId } : { retryId: referId };
    localStorage.removeItem("deepSearchTime");
    localStorage.removeItem("thinkingSearchTime");
    const body: Record<string, any> = {
      messages: [],
      ...bodyParams,
    };
    reload({
      body,
    });
  });

  // 存在未结束的流，重新调用stream
  useEffect(() => {
    if (!reconnectId || loginLoading) {
      return;
    }
    setTimeout(() => {
      chatReload();
    }, 1000);
    // 离开页面的时候，重置 deepsearch 值
    return () => {
      ChatStore.isDeepsearch = false;
    };
  }, [reconnectId, loginLoading]);

  return {
    messages: formatMessages,
    input,
    handleInputChange,
    handleSubmit,
    appendMessage,
    appendToolMessage,
    appendSaveGoalConfirmed,
    messageIds,
    isLoading,
    stop,
    setInput,
    reload: chatReload,
    error,
    chat: (chatDetailData as any)?.chat,
    chatId,
    referId,
    chatDetailLoading,
    fetchChatDetailPreviousPage,
    hasChatDetailPreviousPage,
    chatDetailFetchingPreviousPage,
  };
};
